import { useBrandContext } from 'context/BrandProvider';
import {
  Close,
  ColorFilterOutline,
  ImageOutline,
  Options,
} from 'react-ionicons';
import { useNavigate } from 'react-router-dom';
import DesignerItem from './DesignerItem';
import { GraniteSelect } from 'components/Select/Select';
import Uploader from './logo/Uploader';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { BrandFormSchema, BrandFormType, BrandType } from 'api/brand/schema';
import { zodResolver } from '@hookform/resolvers/zod';
import { FC, useEffect, useRef, useState } from 'react';
import ColorInput from './ColorInput';
import {
  defaultBrand,
  googleFonts,
  subDomain,
  systemFonts,
} from 'api/brand/utils';
import Divider from 'components/Divider';
import { SingleValue } from 'react-select';
import { GraniteInput } from 'components/V2/Input/GraniteInput';
import { postBrand, putBrand } from 'api/brand/api';
import { useMutation } from 'react-query';
import { useAuthUser } from 'hooks/useAuthUser';
import DesignerSidebarFooter from './DesignerSidebarFooter';

const modeOptions = [
  { label: 'Dark', value: 'dark' },
  { label: 'Light', value: 'light' },
];

// Font family options
const fontFamilyOptions = [
  ...googleFonts.map((font) => ({ value: font, label: font })),
  ...systemFonts.map((font) => ({ value: font, label: font })),
];

const lpBackgroundImageOptions = [
  { label: 'Abstract 1', value: 'abstract_1' },
  { label: 'Abstract 2', value: 'abstract_2' },
  { label: 'Abstract 3', value: 'abstract_3' },
  { label: 'Abstract 4', value: 'abstract_4' },
];

const lpAccentImageOptions = [
  { label: 'Vortex', value: 'vortex' },
  { label: 'Abstract globe', value: 'abstractGlobe' },
  { label: 'Hexagon', value: 'hexagon' },
  { label: 'Dot pulse', value: 'dotPulse' },
  { label: 'Prism', value: 'prism' },
  { label: 'Upload my own', value: 'custom' },
];

const viewTypeOptions: ViewType[] = [
  { label: 'New user', value: 'new-user' },
  { label: 'Existing user', value: 'existing-user' },
];
type ViewType =
  | { label: 'New user'; value: 'new-user' }
  | { label: 'Existing user'; value: 'existing-user' };

interface DesignerSidebarProps {
  data?: BrandType | null;
  brandId?: string;
}
const DesignerSidebar: FC<DesignerSidebarProps> = ({ data, brandId }) => {
  const { companyName, group_id } = useAuthUser();
  const brandIdRef = useRef(brandId);
  const [viewType, setViewType] = useState<ViewType>(viewTypeOptions[0]);
  const { onUpdateBrand, onChangeRoute, onResetBrand, onParentUpdateBrand } =
    useBrandContext();
  const navigate = useNavigate();

  const onClose = () => navigate('/');

  const onChangeViewType = (option: SingleValue<ViewType>) => {
    if (option) {
      setViewType(option);
      if (option.value === 'new-user') onChangeRoute('/white-label');
      else if (option.value === 'existing-user') onChangeRoute('/');
    }
  };

  const { reset, handleSubmit, control, ...methods } = useForm<BrandFormType>({
    resolver: zodResolver(BrandFormSchema),
  });

  const {
    mutate: addBrand,
    isLoading: adding,
    isSuccess: adddingSuccess,
  } = useMutation(postBrand, {
    onSuccess: (res) => {
      brandIdRef.current = res.id;
      onParentUpdateBrand(res.config_data);
    },
  });

  const {
    mutate: updateBrand,
    isLoading: updating,
    isSuccess: updatingSuccess,
  } = useMutation(putBrand, {
    onSuccess: (res) => {
      brandIdRef.current = res.id;
      onParentUpdateBrand(res.config_data);
    },
  });

  const onRestoreSuccess = () => {
    brandIdRef.current = undefined;
    const _data = {
      ...defaultBrand,
      lpAccentImageCustom: null,
      logo: null,
    };
    reset(_data);
    onResetBrand(_data);
    onParentUpdateBrand(_data);
  };

  useEffect(() => {
    if (data)
      reset({
        ...data,
        logo: data?.logo
          ? {
              asset_name: data?.logoName ?? undefined,
              file_size: data?.logoSize ?? undefined,
              id: data?.logoId ?? undefined,
              url: data?.logo ?? undefined,
            }
          : null,
        lpAccentImageCustom: data?.lpAccentImageCustom
          ? {
              asset_name: data?.lpAccentImageCustomName ?? undefined,
              file_size: data?.lpAccentImageCustomSize ?? undefined,
              id: data?.lpAccentImageCustomId ?? undefined,
              url: data?.lpAccentImageCustom ?? undefined,
            }
          : null,
      });
  }, [data, reset]);

  const onSubmit = async ({
    logo,
    lpAccentImageCustom,
    ...brand
  }: BrandFormType) => {
    const newBrand: BrandType = {
      ...(process.env.REACT_APP_CURRENT_ENV === 'dev'
        ? { groupId: group_id, groupName: companyName }
        : {}),
      ...brand,
      ...(logo?.url
        ? {
            logo: logo?.url,
            logoName: logo?.asset_name,
            logoId: logo?.id,
            logoSize: logo?.file_size,
          }
        : {}),
      ...(lpAccentImageCustom
        ? {
            lpAccentImageCustom: lpAccentImageCustom?.url,
            lpAccentImageCustomName: lpAccentImageCustom?.asset_name,
            lpAccentImageCustomId: lpAccentImageCustom?.id,
            lpAccentImageCustomSize: lpAccentImageCustom?.file_size,
          }
        : {}),
      title: `Serviceability Tool | ${companyName}`,
    };
    const body = {
      company_name: companyName,
      company_slug: subDomain,
      config_data: newBrand,
    };
    reset({ ...brand, logo, lpAccentImageCustom });
    if (brandIdRef.current) updateBrand(body);
    else addBrand(body);
  };

  return (
    <div className="z-10 min-w-[320px] max-w-[320px] rounded bg-background-base-surface-1 py-6 pl-6 shadow-[15px_4px_24px_0px_#00000040]">
      <FormProvider {...{ ...methods, control, reset, handleSubmit }}>
        <form
          className="flex h-full  flex-col justify-between gap-8 "
          onSubmit={handleSubmit(onSubmit)}
        >
          <div className="flex items-center justify-between pr-6 text-content-base-default">
            <p className="text-2xl font-bold">Theme designer</p>{' '}
            <span className="cursor-pointer" onClick={onClose}>
              <Close width="24px" height="24px" color="currentColor" />
            </span>
          </div>
          <div className="pr-6">
            <GraniteSelect
              label="View type"
              onChange={onChangeViewType}
              value={viewType}
              options={viewTypeOptions}
            />
          </div>
          <Divider className="mr-6" />
          <div className="vertical-scrollbar flex-1">
            <div className="flex flex-col gap-6 pr-[13px]">
              <DesignerItem
                icon={
                  <ImageOutline
                    width="24px"
                    height="24px"
                    color="currentColor"
                  />
                }
                title="Logo"
              >
                <Controller
                  control={control}
                  name="logo"
                  render={({ field }) => {
                    return (
                      <Uploader
                        uploadedFile={field.value ?? null}
                        assetType="logo"
                        onChange={async (file) => {
                          field.onChange(file);
                          onUpdateBrand({
                            logo: file?.url,
                            logoName: file?.asset_name,
                            logoId: file?.id,
                            logoSize: file?.file_size ?? undefined,
                          });
                        }}
                      />
                    );
                  }}
                />
              </DesignerItem>
              <DesignerItem
                icon={
                  <ColorFilterOutline
                    width="24px"
                    height="24px"
                    color="currentColor"
                  />
                }
                title="Theme"
              >
                <Controller
                  control={control}
                  name="mode"
                  render={({ field }) => (
                    <GraniteSelect
                      {...field}
                      onChange={(option) => {
                        field.onChange(option?.value);
                        option?.value &&
                          onUpdateBrand({
                            mode: option.value as 'dark' | 'light',
                          });
                      }}
                      value={modeOptions.find((m) => m.value === field.value)}
                      options={modeOptions}
                    />
                  )}
                />
              </DesignerItem>
              <DesignerItem
                icon={
                  <Options width="24px" height="24px" color="currentColor" />
                }
                title="Custom"
              >
                <div className="flex flex-col gap-6">
                  <ColorInput name="backgroundColor" label="Background color" />
                  <ColorInput name="primaryColor" label="Primary color" />
                  <ColorInput name="accentColor" label="Accent color" />
                  <ColorInput name="strokeColor" label="Stroke color" />
                  <ColorInput name="fontColor" label="Font color" />
                  <Controller
                    control={control}
                    name="fontFamily"
                    render={({ field }) => (
                      <GraniteSelect
                        {...field}
                        label="Font family"
                        onChange={(option) => {
                          field.onChange(option?.value);
                          option?.value &&
                            onUpdateBrand({ fontFamily: option.value });
                        }}
                        value={fontFamilyOptions.find(
                          (m) => m.value === field.value,
                        )}
                        options={fontFamilyOptions}
                      />
                    )}
                  />
                </div>
              </DesignerItem>
              {viewType.value === 'new-user' ? (
                <DesignerItem icon={<DashboardSvg />} title="Landing page">
                  <div className="flex flex-col gap-6">
                    <GraniteInput
                      label="Tagline top row"
                      innerInputClassName="w-full"
                      className="w-full"
                      error={methods.formState.errors.lpTaglineTopRow?.message}
                      {...methods.register('lpTaglineTopRow', {
                        onChange: (event) => {
                          const value = event.target.value;
                          methods.setValue('lpTaglineTopRow', value, {
                            shouldValidate: true,
                          });
                          onUpdateBrand({
                            lpTaglineTopRow: value.slice(0, 40),
                          });
                        },
                      })}
                    />
                    <GraniteInput
                      label="Tagline bottom row"
                      innerInputClassName="w-full"
                      className="w-full"
                      error={
                        methods.formState.errors.lpTaglineBottomRow?.message
                      }
                      {...methods.register('lpTaglineBottomRow', {
                        onChange: (event) => {
                          const value = event.target.value;
                          methods.setValue('lpTaglineBottomRow', value, {
                            shouldValidate: true,
                          });
                          onUpdateBrand({
                            lpTaglineBottomRow: value.slice(0, 30),
                          });
                        },
                      })}
                    />
                    <GraniteInput
                      label="Subtext"
                      innerInputClassName="w-full"
                      className="w-full"
                      error={methods.formState.errors.lpSubtext?.message}
                      {...methods.register('lpSubtext', {
                        onChange: (event) => {
                          const value = event.target.value;
                          methods.setValue('lpSubtext', value, {
                            shouldValidate: true,
                          });
                          onUpdateBrand({ lpSubtext: value.slice(0, 70) });
                        },
                      })}
                    />
                    <Controller
                      control={control}
                      name="lpBackgroundImage"
                      render={({ field }) => (
                        <GraniteSelect
                          {...field}
                          label="Background image"
                          onChange={(option) => {
                            field.onChange(option?.value);
                            option?.value &&
                              onUpdateBrand({
                                lpBackgroundImage:
                                  option.value as BrandType['lpBackgroundImage'],
                              });
                          }}
                          value={lpBackgroundImageOptions.find(
                            (m) => m.value === field.value,
                          )}
                          options={lpBackgroundImageOptions}
                        />
                      )}
                    />
                    <Controller
                      control={control}
                      name="lpAccentImage"
                      render={({ field }) => (
                        <GraniteSelect
                          {...field}
                          label="Accent Image"
                          menuPlacement={
                            field.value === 'custom' ? 'auto' : 'top'
                          }
                          onChange={(option) => {
                            field.onChange(option?.value);
                            option?.value &&
                              onUpdateBrand({
                                lpAccentImage:
                                  option.value as BrandType['lpAccentImage'],
                              });
                          }}
                          value={lpAccentImageOptions.find(
                            (m) => m.value === field.value,
                          )}
                          options={lpAccentImageOptions}
                        />
                      )}
                    />
                    {methods.watch('lpAccentImage') === 'custom' ? (
                      <Controller
                        control={control}
                        name="lpAccentImageCustom"
                        render={({ field, fieldState: { error } }) => {
                          return (
                            <Uploader
                              uploadedFile={field.value ?? null}
                              assetType="custom_accent_image"
                              label="Accent image upload"
                              error={error?.message}
                              onChange={async (file) => {
                                methods.setValue('lpAccentImage', 'custom');
                                field.onChange(file);
                                onUpdateBrand({
                                  lpAccentImageCustom: file?.url,
                                  lpAccentImageCustomName: file?.asset_name,
                                  lpAccentImageCustomId: file?.id,
                                  lpAccentImageCustomSize: file?.file_size,
                                });
                              }}
                            />
                          );
                        }}
                      />
                    ) : null}
                  </div>
                </DesignerItem>
              ) : null}
            </div>
          </div>
          <DesignerSidebarFooter
            success={adddingSuccess || updatingSuccess}
            loading={adding || updating}
            onClose={onClose}
            brandId={brandIdRef.current}
            onRestoreSuccess={onRestoreSuccess}
          />
        </form>
      </FormProvider>
    </div>
  );
};

export default DesignerSidebar;

const DashboardSvg = () => {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M3.5 22H21.5C21.7652 22 22.0196 21.8946 22.2071 21.7071C22.3946 21.5196 22.5 21.2652 22.5 21V3C22.5 2.73478 22.3946 2.48043 22.2071 2.29289C22.0196 2.10536 21.7652 2 21.5 2H3.5C3.23478 2 2.98043 2.10536 2.79289 2.29289C2.60536 2.48043 2.5 2.73478 2.5 3V21C2.5 21.2652 2.60536 21.5196 2.79289 21.7071C2.98043 21.8946 3.23478 22 3.5 22ZM4.5 10H6.5V20H4.5V10ZM20.5 20H8.5V10H20.5V20ZM4.5 4H20.5V8H4.5V4Z"
        fill="currentColor"
      />
    </svg>
  );
};
